import { useState, useEffect, useCallback, useRef } from 'react';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';

/**
 * useAuth
 *
 * Single source of truth for Firebase Authentication state.
 * Exposes the current user and login/logout actions.
 * All other hooks that need the current user receive it as an argument.
 *
 * @param {object}   options
 * @param {function} options.onLogin  - Fired on login. Receives the Firebase user object.
 * @param {function} options.onLogout - Fired on logout.
 */
export function useAuth({ onLogin, onLogout } = {}) {
  const [user, setUser] = useState(null);

  // Refs keep the latest callbacks accessible inside the auth listener
  // without requiring the effect to re-run when they change.
  const onLoginRef  = useRef(onLogin);
  const onLogoutRef = useRef(onLogout);

  useEffect(() => {
    onLoginRef.current  = onLogin;
    onLogoutRef.current = onLogout;
  }, [onLogin, onLogout]);

  // One listener for the lifetime of the app — canonical Firebase pattern.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        onLoginRef.current?.(firebaseUser);
      } else {
        setUser(null);
        onLogoutRef.current?.();
      }
    });

    return unsubscribe;
  }, []);

  const handleLogin = useCallback(async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      // No setUser needed — onAuthStateChanged handles it automatically.
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      // No setUser needed — onAuthStateChanged handles it automatically.
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, []);

  return { user, handleLogin, handleLogout };
}